// ============================================================================
// Conexión con el servidor (modo contingencia, paso 2 · oct-2026)
//
// Lógica pura del indicador de conexión y de los reintentos con llave, sin
// React ni fetch, para poder probarla con node:
//   node apps/web/src/lib/conexion.test.ts
//
// La usan app/lib/monitor-conexion.ts (estado vivo + ping) y api/client.ts
// (postIdempotente).
//
// LA REGLA: el indicador mide "guardado", no "hay wifi". El navegador dice que
// está en línea con wifi aunque no haya internet, y "en línea" con cambios sin
// confirmar tampoco es "todo guardado". Verde solo cuando el servidor respondió
// hace poco Y no hay nada esperando confirmación. Nunca arranca en verde.
// ============================================================================

/** Cada cuánto se pregunta /api/ping con todo en orden. */
export const PING_CADA_MS = 10_000;
/** Después de un ping fallido se vuelve a preguntar antes, para confirmar. */
export const PING_TRAS_FALLO_MS = 5_000;
export const PING_TIMEOUT_MS = 4_000;
/** Un "sí" del servidor vale este tiempo; pasado, volvemos a "Comprobando…". */
export const VIGENCIA_OK_MS = 15_000;
/** Pings fallidos seguidos para declarar "Sin conexión" (unos 20 s). */
export const FALLOS_PARA_SIN_CONEXION = 3;
/** Un guardado más rápido que esto no pinta ámbar: evita un parpadeo por clic. */
export const GUARDANDO_VISIBLE_DESDE_MS = 800;
/** El tiempo real puede tardar esto en (re)conectar sin que se avise. */
export const TIEMPO_REAL_GRACIA_MS = 10_000;

/** Timeout de cada intento de un cambio con llave (crear, agregar, facturar, pagar). */
export const GUARDAR_TIMEOUT_MS = 15_000;
export const GUARDAR_INTENTOS = 3;
/** Espera antes del 2.º y del 3.er intento. */
export const ESPERAS_REINTENTO_MS = [1_000, 3_000];

export type EstadoConexion = 'comprobando' | 'en_linea' | 'guardando' | 'pendiente_local' | 'sin_vivo' | 'sin_conexion';
/** 'sin_uso' = ninguna pantalla escucha eventos en vivo: no hay nada que avisar. */
export type EstadoTiempoReal = 'sin_uso' | 'conectando' | 'abierto' | 'caido';

export interface FotoConexion {
  ahora: number;
  /** Última vez que el servidor respondió (ping o cualquier llamada). */
  ultimoOk: number | null;
  fallosSeguidos: number;
  /** Cuándo empezó cada cambio con llave que todavía no tiene respuesta. */
  guardandoDesde: number[];
  /** Última vez que un cambio con llave quedó confirmado por el servidor. */
  ultimoGuardadoOk: number | null;
  /** Pedidos de la caja de contingencia que siguen solo en este equipo. */
  pedidosLocales: number;
  tiempoReal: { estado: EstadoTiempoReal; desde: number };
}

export function tiempoRealCaido(f: Pick<FotoConexion, 'ahora' | 'tiempoReal'>): boolean {
  if (f.tiempoReal.estado === 'caido') return true;
  return f.tiempoReal.estado === 'conectando' && f.ahora - f.tiempoReal.desde > TIEMPO_REAL_GRACIA_MS;
}

/**
 * El estado que muestra el indicador. El orden importa:
 *   1. Sin conexión gana a todo: lo que se haga ahora no se guarda.
 *   2. Un guardado en curso (y que ya tardó) se ve aunque el ping esté viejo.
 *   3. Sin una respuesta reciente no sabemos nada: gris, nunca verde.
 *   4. Pedidos de contingencia solo en este equipo: ámbar. No es "todo guardado".
 *   5. Servidor bien pero sin eventos en vivo: ámbar.
 */
export function estadoConexion(f: FotoConexion): EstadoConexion {
  if (f.fallosSeguidos >= FALLOS_PARA_SIN_CONEXION) return 'sin_conexion';
  if (f.guardandoDesde.some(t => f.ahora - t >= GUARDANDO_VISIBLE_DESDE_MS)) return 'guardando';
  if (f.ultimoOk == null || f.fallosSeguidos > 0 || f.ahora - f.ultimoOk > VIGENCIA_OK_MS) return 'comprobando';
  if (f.pedidosLocales > 0) return 'pendiente_local';
  if (tiempoRealCaido(f)) return 'sin_vivo';
  return 'en_linea';
}

/**
 * Respuestas que no prueban nada sobre el cambio: sin red (0) o el proxy sin
 * respuesta de la API (502/503/504). Con llave se puede reintentar sin miedo.
 * Un 400 o un 422 es una respuesta real: no se reintenta.
 */
export function esFallaDeRed(status: number): boolean {
  return status === 0 || status === 502 || status === 503 || status === 504;
}

/**
 * Corre `hacer` hasta `intentos` veces mientras el error sea reintentable.
 * `hacer` recibe el número de intento y si es el último (para no avisar fallas
 * intermedias). Solo para operaciones idempotentes: con llave, un reintento
 * nunca duplica (ver apps/api/src/lib/idempotencia.js).
 */
export async function conReintentos<T>(
  hacer: (intento: number, ultimo: boolean) => Promise<T>,
  opts: {
    reintentable: (e: unknown) => boolean;
    intentos?: number;
    esperas?: number[];
    esperar?: (ms: number) => Promise<void>;
  },
): Promise<T> {
  const intentos = opts.intentos ?? GUARDAR_INTENTOS;
  const esperas = opts.esperas ?? ESPERAS_REINTENTO_MS;
  const esperar = opts.esperar ?? ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));
  for (let n = 1; ; n++) {
    const ultimo = n >= intentos;
    try {
      return await hacer(n, ultimo);
    } catch (e) {
      if (ultimo || !opts.reintentable(e)) throw e;
      await esperar(esperas[Math.min(n - 1, esperas.length - 1)] ?? 0);
    }
  }
}

type CryptoMinimo = {
  randomUUID?: () => string;
  getRandomValues?: (a: Uint8Array) => Uint8Array;
};

/**
 * UUID v4 para el header Idempotency-Key. `crypto.randomUUID` solo existe en
 * contextos seguros (https o localhost): abierto por IP de la red local no
 * está, y el servidor rechaza una llave que no sea UUID. Por eso el respaldo
 * arma el UUID a mano con getRandomValues, que sí existe en todos lados.
 */
export function nuevaLlave(c: CryptoMinimo | undefined = (globalThis as { crypto?: CryptoMinimo }).crypto): string {
  if (typeof c?.randomUUID === 'function') {
    try { return c.randomUUID(); } catch { /* cae al respaldo */ }
  }
  const b = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; // versión 4
  b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
